import {createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../store';
import {Media} from '../../types';

export interface MediaState {
  currentMediaPlaying?: string;
  currentMediaId?: string;
  isMediaPlaying?: boolean;
  isMediaPaused?: boolean;
  isMediaPreloadingPlayback?: boolean;
  isMediaDownloading?: boolean;
  isLoadingMedia?: boolean;
  currentMedia?: Media;
}

export enum MediaActionType {
  stop = 'stop',
  play = 'play',
  loaded = 'loaded',
  pause = 'pause',
}

export interface MediaPlayAction {
  actionType: MediaActionType.play;
  mediaId: string;
  media: Media;
}
export interface MediaPauseAction {
  actionType: MediaActionType.pause;
  pause: boolean;
}

export interface MediaLoadedAction {
  actionType: MediaActionType.loaded;
  mediaId: string;
}

export type MediaGenericActions = MediaActionType.stop;

export type MediaAction =
  | {actionType: MediaGenericActions}
  | MediaPlayAction
  | MediaLoadedAction;

const initialState: MediaState = {};

export const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    stop: state => {
      state.isLoadingMedia = undefined;
      state.currentMediaId = undefined;
    },
    play: (state, action: PayloadAction<MediaPlayAction>) => {
      state.currentMediaId = action.payload.mediaId;
      state.currentMedia = action.payload.media;
      state.isLoadingMedia = true;
    },
    togglePause: (state, action: PayloadAction<MediaPauseAction>) => {
      state.isMediaPaused = action.payload.pause;
    },
    // Use the PayloadAction type to declare the contents of `action.payload`
    loaded: (state, _action: PayloadAction<MediaLoadedAction>) => {
      state.isLoadingMedia = false;
      state.isMediaPlaying = true;
    },
  },
});

export const {stop, play, loaded, togglePause} = mediaSlice.actions;

export const selectCurrentMediaId = (state: RootState) =>
  state.media.currentMediaId;

export const selectIsLoadingMedia = (state: RootState) =>
  state.media.isLoadingMedia;

export const selectIsPlayingMedia = (state: RootState) =>
  state.media.isMediaPlaying;

export default mediaSlice.reducer;
